import { useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, MessageCircle, MapPin, ShieldCheck, Clock, Fuel } from "lucide-react";

const HERO_BG =
  "https://images.unsplash.com/photo-1760127831098-6a75c69ee79c?crop=entropy&cs=srgb&fm=jpg&q=85";

const DISTRICTS = [
  { name: "Selçuklu", eta: "~15 dk" },
  { name: "Karatay", eta: "~15 dk" },
  { name: "Meram", eta: "~20 dk" },
  { name: "Çumra", eta: "~30 dk" },
  { name: "Cihanbeyli", eta: "~40 dk" },
  { name: "Kulu", eta: "~45 dk" },
  { name: "Ilgın", eta: "~45 dk" },
  { name: "Seydişehir", eta: "~45 dk" },
  { name: "Akşehir", eta: "~55 dk" },
  { name: "Beyşehir", eta: "~55 dk" },
  { name: "Ereğli", eta: "~70 dk" },
  { name: "İl Dışı", eta: "Telefonda netleşir" },
];

const WA_URL =
  "https://wa.me/905392196017?text=Merhaba%20Konya%20%C3%87ekicim,%20yolda%20kald%C4%B1m.%20Konum%20payla%C5%9Fmak%20istiyorum.";

const lineAnim = (delay) => ({
  initial: { y: "112%" },
  animate: { y: "0%" },
  transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1], delay },
});

export default function Hero() {
  const [active, setActive] = useState(0);
  const { scrollY } = useScroll();
  const bgY = useTransform(scrollY, [0, 700], [0, 140]);
  const district = DISTRICTS[active];

  return (
    <section
      id="anasayfa"
      data-testid="hero-section"
      className="relative min-h-[100svh] flex items-center overflow-hidden pt-28 pb-14 lg:pt-32 lg:pb-20"
    >
      <motion.div style={{ y: bgY }} className="absolute inset-0 -z-10" aria-hidden="true">
        <img
          src={HERO_BG}
          alt=""
          className="w-full h-[120%] object-cover object-center opacity-40"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-night-900/70 via-night-900/60 to-night-900" />
        <div className="absolute inset-0 bg-gradient-to-r from-night-900 via-night-900/40 to-transparent" />
        <div className="absolute -top-24 right-0 w-[520px] h-[520px] rounded-full bg-brand/15 blur-[140px]" />
      </motion.div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.05 }}
            className="inline-flex items-center gap-2.5 rounded-full border border-wa/40 bg-wa/10 px-4 py-1.5 mb-6"
          >
            <span className="relative flex w-2.5 h-2.5" aria-hidden="true">
              <span className="absolute inline-flex w-full h-full rounded-full bg-wa opacity-60 animate-ping" />
              <span className="relative inline-flex w-2.5 h-2.5 rounded-full bg-wa" />
            </span>
            <span className="text-xs uppercase tracking-[0.2em] text-wa font-semibold">
              Şu an nöbetçiyiz · 7/24 açık
            </span>
          </motion.div>

          <h1 className="font-display font-extrabold uppercase leading-[0.92] text-[clamp(3.4rem,10vw,7.8rem)] tracking-tight">
            <span className="block overflow-hidden pb-1">
              <motion.span className="block text-white" {...lineAnim(0.15)}>
                Yolda mı
              </motion.span>
            </span>
            <span className="block overflow-hidden pb-2">
              <motion.span className="block text-outline" {...lineAnim(0.28)}>
                kaldınız?
              </motion.span>
            </span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.45 }}
            className="mt-5 text-base sm:text-lg text-slate-300 max-w-xl leading-relaxed"
          >
            <strong className="text-white">Adem ILDIRAR</strong> – Konya Çekicim. İl içi ve il dışı
            profesyonel çekici hizmeti. Merkez ilçelerde ortalama{" "}
            <span className="text-brand font-semibold">15-20 dakikada</span> yanınızdayız; gece,
            gündüz, bayram fark etmez.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.58 }}
            className="mt-8 flex flex-col sm:flex-row gap-3 sm:gap-4"
          >
            <a
              href="tel:+905392196017"
              data-testid="hero-call-button"
              className="group inline-flex items-center justify-center gap-3 bg-brand hover:bg-brand-light text-night-900 rounded-full px-7 py-4 transition-all duration-200 animate-pulse-ring hover:animate-none active:scale-95"
            >
              <Phone className="w-5 h-5 transition-transform duration-200 group-hover:rotate-12" strokeWidth={2.5} />
              <span className="font-display font-bold text-2xl tracking-wide">0539 219 60 17</span>
            </a>
            <a
              href={WA_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-whatsapp-button"
              className="inline-flex items-center justify-center gap-3 rounded-full px-7 py-4 border border-wa/50 text-wa hover:bg-wa hover:text-night-900 transition-all duration-200 active:scale-95"
            >
              <MessageCircle className="w-5 h-5" strokeWidth={2.5} />
              <span className="font-display font-semibold text-xl tracking-wide uppercase">WhatsApp'tan Konum At</span>
            </a>
          </motion.div>

          <motion.ul
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.75 }}
            className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-400"
          >
            {[
              { icon: ShieldCheck, text: "Hasarsız ve güvenli taşıma" },
              { icon: Clock, text: "7/24 kesintisiz nöbet" },
              { icon: Fuel, text: "Telefonda net fiyat" },
            ].map(({ icon: Icon, text }) => (
              <li key={text} className="flex items-center gap-2">
                <Icon className="w-4 h-4 text-brand" strokeWidth={2.5} />
                {text}
              </li>
            ))}
          </motion.ul>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 32 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
          className="lg:col-span-5"
        >
          <div data-testid="dispatch-calculator" className="glass-card rounded-2xl overflow-hidden">
            <div className="hazard-stripes h-1.5 w-full" aria-hidden="true" />
            <div className="p-6 sm:p-7">
              <div className="flex items-center gap-2 text-brand">
                <MapPin className="w-4 h-4" strokeWidth={2.5} />
                <span className="text-xs font-semibold uppercase tracking-[0.25em]">Acil çağrı simülatörü</span>
              </div>
              <h2 className="font-display font-bold uppercase text-2xl mt-2 text-white">
                Nerede kaldınız?
              </h2>
              <div className="mt-4 flex flex-wrap gap-2">
                {DISTRICTS.map((d, i) => (
                  <button
                    key={d.name}
                    onClick={() => setActive(i)}
                    data-testid={`district-chip-${i}`}
                    aria-pressed={active === i}
                    className={`rounded-full px-3.5 py-1.5 text-sm transition-all duration-200 border ${
                      active === i
                        ? "bg-brand border-brand text-night-900 font-bold shadow-[0_0_18px_rgba(255,107,0,0.4)]"
                        : "border-white/15 text-slate-300 hover:border-brand/60 hover:text-brand"
                    }`}
                  >
                    {d.name}
                  </button>
                ))}
              </div>
              <div className="mt-5 rounded-xl bg-night-900/70 border border-white/10 p-4 flex items-center justify-between gap-4">
                <div>
                  <p className="text-xs uppercase tracking-widest text-slate-400">Tahmini varış</p>
                  <p className="font-display font-bold text-3xl text-brand mt-0.5" data-testid="dispatch-eta-result">
                    {district.eta}
                  </p>
                </div>
                <span className="text-xs text-slate-500 text-right max-w-[130px]">
                  Tahmini değerdir; konuma göre değişebilir.
                </span>
              </div>
              <a
                href="tel:+905392196017"
                data-testid="dispatch-call-button"
                className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-brand hover:bg-brand-light text-night-900 rounded-xl py-3.5 font-display font-bold text-xl tracking-wide uppercase transition-all duration-200 active:scale-[0.98]"
              >
                <Phone className="w-5 h-5" strokeWidth={2.5} />
                Bu dakikada arayın
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
